import { z } from 'zod';

import { zodAddressSchema } from '@vestigas/data';

import { EmailSchema } from '@vestigas/utils';

import { zodAdditionalNoteResponseSchema } from '../additionalNote/validations';

/**
 * Validation schemas for fetching a single invoice (internal endpoint).
 */
const zodDecimalValueSchema = z.union([z.string(), z.number()]);

// Reusable schemas
const zodCompanySchema = z.object({
  address: zodAddressSchema,
  companyAccountId: z.uuid(),
  division: z.string().nullish(),
  email: EmailSchema.nullish(),
  id: z.uuid(),
  issuerAssignedId: z.string().nullish(),
  name: z.string(),
  phone: z.string().nullish(),
});

const zodTaxCalculationSchema = z.object({
  basisAmount: zodDecimalValueSchema,
  currency: z.string(),
  percent: zodDecimalValueSchema,
  taxAmount: zodDecimalValueSchema,
  type: z.string(),
});

const zodTotalAmountSchema = z.object({
  currency: z.string().prefault('EUR'),
  dueAmount: zodDecimalValueSchema,
  generalAllowance: zodDecimalValueSchema,
  grossTotal: zodDecimalValueSchema,
  logisticFees: zodDecimalValueSchema,
  netTotal: zodDecimalValueSchema,
  paymentTerms: z.array(z.unknown()).optional(),
  prepaid: zodDecimalValueSchema,
  taxCalculation: z.array(zodTaxCalculationSchema),
  taxTotal: zodDecimalValueSchema,
});

const zodAccountingReferenceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

export const zodInvoiceItemSchema = z.object({
  accountingReferences: z.array(zodAccountingReferenceSchema).optional(),
  additionalNote: zodAdditionalNoteResponseSchema.nullable().optional(),
  additionalQuantity: zodDecimalValueSchema.nullish(),
  additionalUnitType: z.string().nullish(),
  applicableTax: z.string().nullish(),
  buyerOrder: z.string().nullish(),
  costCenter: z.string().nullish(),
  currency: z.string(),
  deliveryNoteAssetIds: z.array(z.uuid()).optional(),
  deliveryNoteNrs: z.array(z.string()).optional(),
  description: z.string().nullish(),
  id: z.number().int(),
  name: z.string(),
  netPrice: zodDecimalValueSchema,
  netPricePerQuantity: zodDecimalValueSchema,
  positionNr: z.number().int(),
  priceTotal: zodDecimalValueSchema,
  quantity: zodDecimalValueSchema,
  sellerAssignedId: z.string().nullish(),
  sellerOrder: z.string().nullish(),
  taxPercent: zodDecimalValueSchema.nullish(),
  unitType: z.string().nullish(),
});

export const zodPriceRawEntrySchema = z.object({
  grossPricePerQuantity: zodDecimalValueSchema,
  netPrice: zodDecimalValueSchema,
  // additionalInformation is an untyped `object` in the OpenAPI baseline (the
  // check-result payload is explicitly experimental and unspecified), and the
  // API has been observed sending null here for entries with no matched unit.
  // Normalized to '' at the boundary so downstream consumers (e.g.
  // getDescriptiveUnit, which already treats '' as "no unit") don't need to
  // special-case null.
  unitType: z
    .string()
    .nullable()
    .transform((value) => value ?? ''),
});

export const zodItemNetPriceMatchesInfoSchema = z.object({
  expectedRaw: z.array(zodPriceRawEntrySchema).optional(),
  invoiceRaw: z.array(zodPriceRawEntrySchema).optional(),
  naReason: z.string().nullish(),
});

export const zodInvoiceCheckResultSchema = z.object({
  additionalInformation: z
    .looseObject({
      itemNetPriceMatches: zodItemNetPriceMatchesInfoSchema.nullish(),
    })
    .nullish(),
  conflictingEntity: z.string().nullish(),
  conflictingValues: z.record(z.string(), z.unknown()).nullish(),
  contractIds: z.array(z.uuid()).nullish(),
  contractItemIds: z.array(z.uuid()).nullish(),
  deliveryNoteAssetId: z.string().nullish(),
  deliveryNoteAssetIds: z.array(z.string()).nullish(),
  deliveryNoteId: z.string().nullish(),
  deliveryNoteNrs: z.array(z.string()).nullish(),
  displayStatus: z.string().nullish(),
  expectedValue: z.string().nullish(),
  ignoreReason: z.string().nullish(),
  invoiceValue: z.string().nullish(),
  itemInvoicePosition: z.number().int().nullish(),
  itemName: z.string().nullish(),
  name: z.string().nullish(),
  productPriceIds: z.array(z.unknown()).nullish(),
  status: z.string().nullish(),
});

export const zodInvoiceMetaSchema = z.object({
  billedDlnItems: z.array(z.unknown()).optional(),
  channel: z.string().optional(),
  checkResults: z.array(zodInvoiceCheckResultSchema).optional(),
  connectedContractIds: z.array(z.uuid()).optional(),
  connectedDeliveryNoteIds: z.array(z.uuid()).optional(),
  datetimeParsed: z.iso.datetime({ offset: true }),
  dnAssetIdList: z.array(z.string()).optional(),
  invoiceNumber: z.string().optional(),
  mailInfo: z.record(z.string(), z.unknown()).optional(),
  pdfAttachmentContent: z.array(z.string()).optional(),
  source: z.string().optional(),
  v: z.string().optional(),
  watermark: z.string().nullish(), // TODO: add watermark enum
});

export const zodInvoiceEmailSchema = z.object({
  fileId: z.uuid().nullish(),
  receivedDatetime: z.iso.datetime({ offset: true }),
  receiverAddress: EmailSchema.nullish(),
  senderAddress: EmailSchema,
  subject: z.string(),
});

export const zodInvoiceFileSchema = z.object({
  contentType: z.string(), // MIME type, e.g. application/pdf
  fileId: z.uuid(),
  fileName: z.string(),
  size: z.number(),
});

export const zodInvoiceFilesSchema = z.object({
  additionalFiles: z.array(zodInvoiceFileSchema).nullish(),
  emails: z.array(zodInvoiceEmailSchema).nullish(),
  invoicePdf: zodInvoiceFileSchema.nullish(),
  originalPdf: zodInvoiceFileSchema.nullish(),
  processedPdf: zodInvoiceFileSchema.nullish(),
});

export const zodInvoiceResponseSchema = z.object({
  additionalNote: zodAdditionalNoteResponseSchema.nullish(),
  buyer: zodCompanySchema,
  currency: z.string().prefault('EUR'),
  date: z.string(),
  datetimeParsed: z.iso.datetime().nullish(),
  files: zodInvoiceFilesSchema.optional(),
  invoiceId: z.uuid(),
  invoiceNr: z.string(),
  items: z.array(zodInvoiceItemSchema),
  meta: zodInvoiceMetaSchema.optional(),
  seller: zodCompanySchema,
  totalAmount: zodTotalAmountSchema,
  watermark: z.string().nullish(), // TODO: add watermark enum
});
